/**
 * @fileoverview Links Panel
 *
 * Combines shape-level linking controls (LinkDiagramTool UI) and diagram-level
 * "Used In" references list into a single sidebar panel.
 *
 * @see Issue #789 - Universal Diagram Linking System
 */

import React from 'react';
import type { ToolRegistry } from '../../core/tools/ToolRegistry';
import type { IToolContext } from '../../core/tools/interfaces/ITool';
import { UsedInPanel } from './UsedInPanel';
import { useTranslation } from '@/hooks/useTranslation';

interface LinksPanelProps {
  diagramId: string;
  toolRegistry?: ToolRegistry;
  toolContext?: IToolContext;
  onToolExecute?: (toolId: string, params?: any) => void;
}

export const LinksPanel: React.FC<LinksPanelProps> = ({ diagramId, toolRegistry, toolContext, onToolExecute }) => {
  const { t } = useTranslation('diagram');
  const linkTool = React.useMemo(() => toolRegistry?.getTool('link-diagram') ?? null, [toolRegistry]);
  const LinkToolUI = linkTool?.getPropertyPanelComponent?.();

  return (
    <div className="pb-4">
      {LinkToolUI && toolContext ? (
        <LinkToolUI tool={linkTool as any} context={toolContext as any} onExecute={(params: any) => onToolExecute?.('link-diagram', params)} />
      ) : (
        <div className="px-4 py-3 text-sm text-muted-foreground">
          {t('linksPanel.selectionHint', { defaultValue: 'Select a single shape to link to another diagram.' })}
        </div>
      )}

      <div className="mx-4 my-2 border-t" />

      <UsedInPanel diagramId={diagramId} />
    </div>
  );
};
